import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Eye, Heart, MousePointerClick, Share2, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { Link } from "react-router-dom";

/**
 * Aggregate Blinterest analytics for one business — impressions, views,
 * saves, and click-through numbers so owners see the traffic Blinterest
 * sends them. Renders inside the business dashboard.
 */
export default function BlinterestAnalyticsCard({ business }) {
  const [totals, setTotals] = useState(null);
  const [topPins, setTopPins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!business?.business_id) return;
    setLoading(true);
    axios
      .get(`${API}/blinterest/business/${business.business_id}/analytics`, { withCredentials: true })
      .then((r) => {
        setTotals(r.data.totals || null);
        setTopPins(r.data.top_pins || []);
      })
      .catch(() => setTotals(null))
      .finally(() => setLoading(false));
  }, [business?.business_id]);

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!totals || totals.pins === 0) return null;

  const totalClicks =
    (totals.business_clicks || 0) +
    (totals.website_clicks || 0) +
    (totals.booking_clicks || 0) +
    (totals.product_clicks || 0);

  const stats = [
    { label: "Views", value: totals.views, icon: Eye },
    { label: "Keeps", value: totals.saves, icon: Heart },
    { label: "Clicks", value: totalClicks, icon: MousePointerClick },
    { label: "Shares", value: totals.shares, icon: Share2 },
  ];

  return (
    <Card className="mb-6" data-testid="blinterest-analytics-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg text-tbc-navy flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-tbc-gold" />
          Blinterest Analytics
          <span className="text-xs text-gray-500 font-body font-normal">
            · {totals.pins} Drop{totals.pins === 1 ? "" : "s"} live
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {stats.map(({ label, value, icon: Icon }) => (
            <div
              key={label}
              className="rounded-xl border border-gray-100 p-3 bg-tbc-cream/40"
              data-testid={`analytics-stat-${label.toLowerCase()}`}
            >
              <div className="flex items-center gap-2 text-gray-500 text-xs">
                <Icon className="w-3.5 h-3.5" />
                {label}
              </div>
              <p className="font-heading text-2xl text-tbc-navy mt-1">
                {(value || 0).toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {topPins.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-body mb-2">
              Top-Performing Drops
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {topPins.map((p) => (
                <Link
                  key={p.pin_id}
                  to={`/blinterest/pin/${p.pin_id}`}
                  className="block rounded-lg overflow-hidden bg-gray-50 group"
                  data-testid={`analytics-top-pin-${p.pin_id}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={resolveAssetUrl(p.image_url)}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-1.5">
                    <p className="text-[10px] text-gray-700 line-clamp-1">{p.title}</p>
                    <p className="text-[10px] text-gray-500">
                      {p.save_count || 0} keeps · {p.view_count || 0} views
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
